// InFocus.jsx
import React, { useState } from 'react';
import { i18n } from 'app/i18n/i18n';
import { push, getQueryString } from 'api/search/filterUrlMaker';
import { useQueryParams } from 'lib/hooks/useQueryParams';
import { getPathname } from 'lib/routing';
import { notAssetClass } from 'pages/redesign/publications/api/searchRequest/useGetFilters';
import { auditOnThemeInFocus } from 'lib/analytics/auditEvents';
import './ThemeInFocus.css';

const DEFAULT_VISIBLE = 5;

const ThemeInFocus = ({ themes = [] }) => {
    const qs = useQueryParams();
    const lang = i18n.getLanguage();

    const activeTheme = getActiveTheme(qs, lang, themes);
    const preselectedBeyondFold = activeTheme ? themes.indexOf(activeTheme) >= DEFAULT_VISIBLE : false;

    const [isExpanded, setIsExpanded] = useState(preselectedBeyondFold);

    if (!themes.length) return null;

    const hasMore = themes.length > DEFAULT_VISIBLE;
    const visibleThemes = isExpanded ? themes : themes.slice(0, DEFAULT_VISIBLE);

    const handleTopicClick = (topic) => {
        auditOnThemeInFocus(topic);
        const pathname = getPathname().replace(/^\//, '');
        const targetPath = notAssetClass.includes(pathname) ? undefined : '/Home';

        if (activeTheme === topic) {
            const qs = getQueryString();
            // Clear all themes.* keys to avoid stale keys from a previous language
            Object.keys(qs).filter(k => k.startsWith('themes.')).forEach(k => delete qs[k]);
            push(qs, targetPath);
        } else {
            push({ [`themes.${lang}`]: [topic] }, targetPath);
        }
    };

    return (
        <div className="in-focus-wrapper">
            <div className="in-focus-header-row">
                <i className="pr-2 fa-fw fa fa-tags" />
                <label className="in-focus-label">{i18n.translate('themes-in-focus')}</label>
            </div>
            <div className="in-focus-container">
            
            <div className="topics-grid">
                {visibleThemes.map((topic, index) => (
                    <button 
                        key={index}
                        type="button"
                        className={`topic-tag ${activeTheme === topic ? 'selected' : ''}`}
                        onClick={() => handleTopicClick(topic)}
                    >
                        {topic}
                    </button>
                ))}
            </div>
            
            {hasMore && (
                <button 
                    type="button"
                    className={`more-button ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <span>{isExpanded ? i18n.translate('Less') : i18n.translate('More')}</span>
                    <span className="chevron"></span>
                </button>
            )}
            </div>
        </div>
    );
};

export default ThemeInFocus;

function getActiveTheme(qs, lang, themes) {
  // Prefer the key matching the current language, then fall back to any themes.* key
  // (e.g. themes.en may still be in the URL after the user switches to JA)
  const keysToTry = [
    `themes.${lang}`,
    ...Object.keys(qs).filter(k => k.startsWith('themes.') && k !== `themes.${lang}`),
  ];
  for (const key of keysToTry) {
    const qsValue = qs[key];
    const active = Array.isArray(qsValue) ? qsValue[0] : qsValue;
    if (themes.includes(active)) return active;
  }
  return null;
}